"use client";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ReceiptView } from "@/components/receipt/ReceiptView";
import { paymentService } from "@/services/paymentService";
import { userService } from "@/services/userService";
import { PaymentOutput } from "@/types/payment.types";
import { Customer, Vehicle } from "@/types";
import { Card, CardContent } from "@/components/ui/card";

export default function ReceiptPage() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const [payment, setPayment] = useState<PaymentOutput | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPayment = async () => {
      if (!paymentId) {
        setError("Payment ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const paymentData = await paymentService.getPaymentById(paymentId);
        setPayment(paymentData);

        // Try to fetch full customer data if ID is available
        if (paymentData.customer?.id) {
          try {
            const customerData = await userService.getCustomer(paymentData.customer.id);
            setCustomer(customerData);
          } catch (err) {
            // If fetching fails (e.g., requires auth), we'll use payment.customer data
            // The payment.customer should have at least name, which is sufficient
            console.warn("Could not fetch full customer data, will use payment.customer:", err);
          }
        }

        // For vehicles, use the data from payment.vehicle if available
        // The payment.vehicle should have make and model which is sufficient
        if (paymentData.vehicle) {
          // Convert single vehicle to array for consistent handling
          setVehicles([paymentData.vehicle as Vehicle]);
        }
      } catch (err) {
        console.error("Error fetching payment:", err);
        setError("Failed to load payment receipt. Please check the link and try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchPayment();
  }, [paymentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
            <p className="text-muted-foreground">Loading receipt...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Receipt Not Found
            </h2>
            <p className="text-muted-foreground mb-4">
              {error || "The payment receipt you're looking for doesn't exist or has been removed."}
            </p>
            <p className="text-sm text-gray-500">
              Please contact support if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <ReceiptView payment={payment} customer={customer} vehicles={vehicles} />;
}

